import type { BugStatus, Guid, Priority, Severity, TestCaseStatus } from './types';

export interface TestCaseFilters {
  suiteId?: Guid | null;
  status?: TestCaseStatus | null;
  priority?: Priority | null;
  search?: string | null;
}
export interface BugFilters {
  status?: BugStatus | null;
  severity?: Severity | null;
  cycleId?: Guid | null;
  search?: string | null;
}

/**
 * Query-key factory. Keys are hierarchical: `[resource, kind, ...ids, filters?]`, so
 * invalidating a prefix (`qk.cycles.all`) refreshes every query below it.
 */
export const qk = {
  me: ['me'] as const,
  devices: ['devices'] as const,

  orgs: {
    all: ['orgs'] as const,
    members: (orgId: Guid) => ['orgs', 'members', orgId] as const,
    partners: (orgId: Guid) => ['orgs', 'partners', orgId] as const,
    integrations: (orgId: Guid) => ['orgs', 'integrations', orgId] as const,
  },

  projects: {
    all: ['projects'] as const,
    list: (orgId: Guid) => ['projects', 'list', orgId] as const,
    detail: (projectId: Guid) => ['projects', 'detail', projectId] as const,
    overview: (projectId: Guid) => ['projects', 'overview', projectId] as const,
  },

  suites: {
    all: ['suites'] as const,
    list: (projectId: Guid) => ['suites', 'list', projectId] as const,
  },

  testCases: {
    all: ['testCases'] as const,
    lists: (projectId: Guid) => ['testCases', 'list', projectId] as const,
    list: (projectId: Guid, filters: TestCaseFilters) => ['testCases', 'list', projectId, filters] as const,
    detail: (caseId: Guid) => ['testCases', 'detail', caseId] as const,
  },

  knownIssues: {
    list: (projectId: Guid) => ['knownIssues', 'list', projectId] as const,
  },

  communities: {
    all: ['communities'] as const,
    list: ['communities', 'list'] as const,
    detail: (id: Guid) => ['communities', 'detail', id] as const,
    invites: (id: Guid) => ['communities', 'invites', id] as const,
    leaderboard: (id: Guid) => ['communities', 'leaderboard', id] as const,
  },

  invites: {
    preview: (token: string) => ['invites', 'preview', token] as const,
  },

  cycles: {
    all: ['cycles'] as const,
    list: (projectId: Guid) => ['cycles', 'list', projectId] as const,
    availableCommunities: (projectId: Guid) => ['cycles', 'availableCommunities', projectId] as const,
    detail: (cycleId: Guid) => ['cycles', 'detail', cycleId] as const,
    board: (cycleId: Guid) => ['cycles', 'board', cycleId] as const,
    report: (cycleId: Guid) => ['cycles', 'report', cycleId] as const,
  },

  my: {
    all: ['my'] as const,
    assignments: ['my', 'assignments'] as const,
    bugs: ['my', 'bugs'] as const,
    reputation: ['my', 'reputation'] as const,
  },

  assignments: {
    detail: (assignmentId: Guid) => ['assignments', 'detail', assignmentId] as const,
  },

  executions: {
    all: ['executions'] as const,
    detail: (executionId: Guid) => ['executions', 'detail', executionId] as const,
    recordingStatus: (executionId: Guid) => ['executions', 'recordingStatus', executionId] as const,
    review: (executionId: Guid) => ['executions', 'review', executionId] as const,
    recording: (executionId: Guid, signature: string) => ['executions', 'recording', executionId, signature] as const,
  },

  reviewQueue: {
    all: ['reviewQueue'] as const,
    list: (projectId: Guid, cycleId?: Guid | null) => ['reviewQueue', projectId, cycleId ?? null] as const,
  },

  logs: {
    all: ['logs'] as const,
    page: (scope: string, filters: object) => ['logs', 'page', scope, filters] as const,
    filters: (scope: string, range: object) => ['logs', 'filters', scope, range] as const,
  },

  team: {
    panel: (teamId: Guid) => ['team', 'panel', teamId] as const,
    offers: (teamId: Guid) => ['team', 'offers', teamId] as const,
    myOffers: ['team', 'myOffers'] as const,
  },
  market: {
    all: ['market'] as const,
    profile: ['market', 'profile'] as const,
    level: ['market', 'level'] as const,
    testers: (filters: object) => ['market', 'testers', filters] as const,
    tester: (userId: Guid) => ['market', 'tester', userId] as const,
    teams: (filters: object) => ['market', 'teams', filters] as const,
    cv: ['market', 'cv'] as const,
    testerCv: (userId: Guid) => ['market', 'testerCv', userId] as const,
    team: (teamId: Guid) => ['market', 'team', teamId] as const,
    teamWork: (teamId: Guid) => ['market', 'teamWork', teamId] as const,
    jobs: (filters: object) => ['market', 'jobs', filters] as const,
    job: (cycleId: Guid) => ['market', 'job', cycleId] as const,
    participations: ['market', 'participations'] as const,
    participants: (cycleId: Guid) => ['market', 'participants', cycleId] as const,
    earnings: ['market', 'earnings'] as const,
    payoutAccount: ['market', 'payoutAccount'] as const,
  },

  inbox: {
    all: ['inbox'] as const,
    counts: ['inbox', 'counts'] as const,
    notifications: (unreadOnly: boolean) => ['inbox', 'notifications', unreadOnly] as const,
    settings: ['inbox', 'settings'] as const,
    alerts: ['inbox', 'alerts'] as const,
  },

  messages: {
    all: ['messages'] as const,
    conversations: ['messages', 'conversations'] as const,
    thread: (conversationId: Guid) => ['messages', 'thread', conversationId] as const,
    questions: (cycleId: Guid) => ['messages', 'questions', cycleId] as const,
  },

  feedback: {
    all: ['feedback'] as const,
    cycle: (cycleId: Guid) => ['feedback', 'cycle', cycleId] as const,
    participant: (participantId: Guid) => ['feedback', 'participant', participantId] as const,
    tester: (userId: Guid) => ['feedback', 'tester', userId] as const,
    team: (teamId: Guid) => ['feedback', 'team', teamId] as const,
    org: (orgId: Guid) => ['feedback', 'org', orgId] as const,
    myDisputes: ['feedback', 'disputes', 'mine'] as const,
    orgDisputes: (orgId: Guid) => ['feedback', 'disputes', 'org', orgId] as const,
    adminDisputes: ['feedback', 'disputes', 'admin'] as const,
  },

  billing: {
    all: ['billing'] as const,
    overview: (orgId: Guid) => ['billing', 'overview', orgId] as const,
    statement: (statementId: Guid) => ['billing', 'statement', statementId] as const,
    admin: ['billing', 'admin'] as const,
    wallet: (orgId: Guid) => ['billing', 'wallet', orgId] as const,
    adminWallet: ['billing', 'adminWallet'] as const,
    annual: (year: number | null) => ['billing', 'annual', year] as const,
    promotion: (target: string, id: Guid) => ['billing', 'promotion', target, id] as const,
    adminPromotions: ['billing', 'adminPromotions'] as const,
    invoices: (orgId: Guid) => ['billing', 'invoices', orgId] as const,
    myInvoices: ['billing', 'myInvoices'] as const,
    invoice: (invoiceId: Guid) => ['billing', 'invoice', invoiceId] as const,
    adminInvoices: (status: string | null) => ['billing', 'adminInvoices', status] as const,
  },

  bugs: {
    all: ['bugs'] as const,
    list: (projectId: Guid, filters: BugFilters) => ['bugs', 'list', projectId, filters] as const,
    similar: (projectId: Guid, title: string) => ['bugs', 'similar', projectId, title] as const,
    similarInCycle: (cycleId: Guid, title: string, actual: string) => ['bugs', 'similarInCycle', cycleId, title, actual] as const,
    duplicateCandidates: (bugId: Guid) => ['bugs', 'duplicateCandidates', bugId] as const,
    detail: (bugId: Guid) => ['bugs', 'detail', bugId] as const,
  },
} as const;
